import React from "react";
import { Eye, EyeOff, Check } from "lucide-react";
import PropTypes from "prop-types";

/**
 * EnhancedInput - Un componente input avanzado que maneja varios tipos de inputs
 * con validación, prefijos, iconos y estados.
 */
const EnhancedInput = ({
  type = "text",
  value,
  onChange,
  placeholder,
  disabled = false,
  className = "",
  prefixComponent = null,
  showPassword = false,
  togglePassword = null,
  validations = null,
  pattern = null,
  inputMode = null,
  required = false,
  autoComplete = "on",
  name = "",
  min = null,
  max = null,
  errorMessage = "",
  inputRef = null,
}) => {
  // Filtrar y formatear inputs numéricos y telefónicos
  const handleNumberInput = (e) => {
    let rawValue = e.target.value.replace(/\D/g, "");
    let formattedValue = rawValue;
    
    // Si es telefónico, dar formato ### ### ####
    if (type === "tel") {
      // Limitar a máximo 10 dígitos
      if (rawValue.length > 10) {
        rawValue = rawValue.slice(0, 10);
      }
      
      // Formatear con espacios: ### ### ####
      if (rawValue.length > 0) {
        if (rawValue.length <= 3) {
          formattedValue = rawValue;
        } else if (rawValue.length <= 6) {
          formattedValue = `${rawValue.slice(0, 3)} ${rawValue.slice(3)}`;
        } else {
          formattedValue = `${rawValue.slice(0, 3)} ${rawValue.slice(3, 6)} ${rawValue.slice(6)}`;
        }
      }
    } 
    // Si es numérico, separar miles
    else if (type === "number") {
      if (rawValue.length > 0) {
        // Formato manual con espacios como separadores de miles
        const num = parseInt(rawValue, 10);
        formattedValue = num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
      }
    }
    
    // Actualizar el valor mostrado y pasar el valor raw para procesamiento
    onChange({ 
      target: { 
        value: formattedValue,
        numericValue: rawValue
      } 
    });
  };

  // Prevenir cambio de valor con la rueda del ratón
  const handleWheel = (e) => {
    if (type === 'number') {
      e.target.blur();
    }
  };

  // Clase base para todos los inputs
  const baseInputClass = `
    bg-gray-50 w-full px-4 py-3 rounded-2xl 
    shadow-sm ring-1 ring-gray-300
    ${disabled ? "opacity-60 cursor-not-allowed bg-gray-100" : ""}
    ${errorMessage ? "ring-red-400" : ""}
    ${className}
  `;
  
  // Estilos específicos para cuando hay un prefijo
  const inputWithPrefixClass = prefixComponent ? "rounded-l-none ring-l-0" : "";
  
  // Estilos para inputs de tipo password
  const passwordInputClass = type === "password" ? "pr-12" : "";

  return (
    <div className="w-full">
      <div className="relative flex">
        {prefixComponent}
        
        <input
          type={type === "password" ? (showPassword ? "text" : "password") : (type === "number" ? "text" : type)}
          value={value}
          onChange={type === "tel" || type === "number" ? handleNumberInput : onChange}
          placeholder={placeholder}
          disabled={disabled}
          className={`${baseInputClass} ${inputWithPrefixClass} ${passwordInputClass}`}
          pattern={pattern}
          inputMode={type === "number" ? "numeric" : inputMode}
          required={required}
          autoComplete={autoComplete}
          name={name}
          min={min}
          max={max}
          ref={inputRef}
          onWheel={handleWheel}
          maxLength={type === "tel" ? 12 : undefined} // 12 incluyendo espacios: "### ### ####"
        />
        
        {/* Icono para mostrar/ocultar contraseña */}
        {type === "password" && togglePassword && (
          <button
            type="button"
            className="absolute inset-y-0 right-3 flex items-center text-blue-600"
            onClick={togglePassword}
            disabled={disabled}
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        )}
      </div>
      
      
      {/* Validaciones (para contraseñas) */}
      {validations && Object.keys(validations).length > 0 && (
        <div className="mt-2 space-y-1 bg-gray-50 p-3 rounded-xl">
          {Object.entries(validations).map(([key, validation]) => (
            <ValidationItem 
              key={key} 
              isValid={validation.isValid} 
              text={validation.text} 
            />
          ))}
        </div>
      )}
    </div>

  );
};

// Componente para cada ítem de validación
const ValidationItem = ({ isValid, text }) => (
  <div className="flex items-center gap-2 text-sm">
    <div
      className={`w-3 h-3 rounded-full ${
        isValid ? "bg-green-500" : "bg-gray-300"
      } flex items-center justify-center`}
    >
      {isValid && <Check size={10} className="text-white" />}
    </div>
    <span className={isValid ? "text-green-700" : "text-gray-600"}>
      {text}
    </span>
  </div>
);

ValidationItem.propTypes = {
  isValid: PropTypes.bool.isRequired,
  text: PropTypes.string.isRequired,
};

EnhancedInput.propTypes = {
  type: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  prefixComponent: PropTypes.node,
  showPassword: PropTypes.bool,
  togglePassword: PropTypes.func,
  validations: PropTypes.object,
  pattern: PropTypes.string,
  inputMode: PropTypes.string,
  required: PropTypes.bool,
  autoComplete: PropTypes.string,
  name: PropTypes.string,
  min: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  max: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  errorMessage: PropTypes.string,
  inputRef: PropTypes.any,
};

export default EnhancedInput;